// src/features/apps/legal/PrivacyDocPreview.tsx

import { Box, Stack, Text, Title } from '@mantine/core';
import type { PrivacyDoc } from '@/lib/types/AppLegal';

interface PrivacyDocPreviewProps {
  doc: PrivacyDoc;
  /** Compact height with its own scrollbar (the wizard); unset = flow. */
  maxHeight?: number;
}

/**
 * Renders a generated policy the way the hosted page will: title, then one
 * heading + paragraphs per section. Plain text only — the document is data,
 * never markup, so what the customer adopts is exactly what viewers read.
 */
export function PrivacyDocPreview({ doc, maxHeight }: PrivacyDocPreviewProps) {
  return (
    <Box
      p="md"
      style={{
        border: '1px solid var(--mantine-color-default-border)',
        borderRadius: 'var(--mantine-radius-md)',
        maxHeight,
        overflowY: maxHeight ? 'auto' : undefined,
        background: 'var(--mantine-color-body)',
      }}
    >
      <Stack gap="md">
        <Title order={4}>{doc.title}</Title>
        {doc.sections.map((s) => (
          <Stack gap={4} key={s.heading}>
            <Text fw={600} size="sm">
              {s.heading}
            </Text>
            {s.paragraphs.map((p, i) => (
              <Text size="sm" key={i} style={{ whiteSpace: 'pre-line' }}>
                {p}
              </Text>
            ))}
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
